import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FloorRoom, RoomDrawTool, RoomPoint } from '../../interface/floor-room';
import {
  clampToBounds,
  distance,
  labelPoint,
  roundPoint,
  snapToRooms,
  centeredSpan,
  polygonContains,
  toSvgPoints
} from '../../utils/polygon';

/**
 * Room colour palette (30). The first 8 must keep their order: rooms saved without a colour
 * fall back to ROOM_COLORS[index % length], so reordering would recolour existing rooms.
 */
export const ROOM_COLORS = [
  // Original palette
  '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#EF4444', '#6366F1',
  // Bright
  '#0EA5E9', '#22C55E', '#EAB308', '#F97316', '#A855F7', '#D946EF', '#06B6D4', '#84CC16', '#F43F5E',
  // Deep
  '#1D4ED8', '#047857', '#B45309', '#6D28D9', '#BE185D', '#0F766E', '#B91C1C', '#4338CA',
  '#0369A1', '#15803D', '#C2410C', '#7E22CE', '#475569'
];

/** Stored colour, or a stable palette colour derived from the room's position in the list. */
export function roomColor(room: FloorRoom, index: number): string {
  return room.color || ROOM_COLORS[index % ROOM_COLORS.length];
}

interface RenderedRoom {
  room: FloorRoom;
  color: string;
  points: string;
  label: RoomPoint;
  /** Largest label font (image units) that keeps the name inside the room at its label point. */
  fitFont: number;
}

/** Screen-pixel sizes; converted to image units so they look the same at every zoom level. */
const SNAP_RADIUS_PX = 10;
const CLOSE_RADIUS_PX = 12;
const VERTEX_RADIUS_PX = 6;
const MIDPOINT_RADIUS_PX = 4.5;
const LABEL_FONT_PX = 13;
/** Below this on-screen size a label is unreadable, so it is hidden (the hover title remains). */
const MIN_LABEL_FONT_PX = 8;
/** Average glyph width / font size for the bold label font; slightly generous on purpose. */
const LABEL_CHAR_WIDTH = 0.62;
const MIN_RECT_SIZE_PX = 12;

/**
 * SVG overlay that renders room boundaries on a floor plan and, when editable, lets the user
 * draw rectangles/polygons and reshape the selected room. Its viewBox is the image's natural
 * pixel size, so room points map 1:1 to device coordinates regardless of zoom or layout.
 *
 * Stateless about persistence: it emits drawn shapes and edited vertices; the parent saves.
 */
@Component({
  selector: 'app-room-boundary-layer',
  imports: [CommonModule],
  templateUrl: './room-boundary-layer.component.html',
  styleUrl: './room-boundary-layer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoomBoundaryLayerComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() rooms: FloorRoom[] = [];
  /** Natural size of the floor plan image (device coordinate space). */
  @Input() imageWidth = 0;
  @Input() imageHeight = 0;
  /** Canvas zoom factor; used to keep handles/labels a constant on-screen size. */
  @Input() zoom = 1;
  @Input() editable = false;
  @Input() tool: RoomDrawTool = 'select';
  @Input() selectedRoomId: string | null = null;
  /** Room to emphasise (e.g. the room a dragged device would land in). */
  @Input() highlightRoomId: string | null = null;
  /** Working copy of the selected room's vertices while it is being reshaped. */
  @Input() editPoints: RoomPoint[] | null = null;

  @Output() editPointsChange = new EventEmitter<RoomPoint[]>();
  @Output() roomSelect = new EventEmitter<string | null>();
  @Output() shapeDrawn = new EventEmitter<RoomPoint[]>();

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  renderedRooms: RenderedRoom[] = [];
  midpoints: RoomPoint[] = [];

  /** Polygon being drawn (click-by-click). */
  draft: RoomPoint[] = [];
  /** Rectangle being dragged out. */
  rectStart: RoomPoint | null = null;
  rectEnd: RoomPoint | null = null;
  cursor: RoomPoint | null = null;
  snapPoint: RoomPoint | null = null;

  /** Image units per on-screen pixel. */
  unitsPerPx = 1;

  private layoutWidth = 0;
  private draggingVertex: number | null = null;
  private resizeObserver?: ResizeObserver;

  constructor(private cdr: ChangeDetectorRef, private ngZone: NgZone) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rooms'] || changes['editPoints'] || changes['selectedRoomId']) {
      this.buildRenderModel();
    }
    if (changes['zoom'] || changes['imageWidth']) {
      this.updateScale();
    }
    if ((changes['editable'] && !this.editable) || changes['tool']) {
      this.cancelDrawing();
    }
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? 0;
      this.ngZone.run(() => {
        this.layoutWidth = width;
        this.updateScale();
        this.cdr.markForCheck();
      });
    });
    this.resizeObserver.observe(this.svgRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  get viewBox(): string {
    return `0 0 ${this.imageWidth} ${this.imageHeight}`;
  }

  get isDrawing(): boolean {
    return this.draft.length > 0 || this.rectStart !== null;
  }

  get canClosePolygon(): boolean {
    return this.draft.length >= 3 && !!this.cursor &&
      distance(this.cursor, this.draft[0]) <= CLOSE_RADIUS_PX * this.unitsPerPx;
  }

  get draftPolyline(): string {
    const points = this.cursor && this.tool === 'polygon' ? [...this.draft, this.cursor] : this.draft;
    return toSvgPoints(points);
  }

  get rectPolygon(): string {
    return this.rectStart && this.rectEnd ? toSvgPoints(this.rectCorners(this.rectStart, this.rectEnd)) : '';
  }

  get vertexRadius(): number { return VERTEX_RADIUS_PX * this.unitsPerPx; }
  get midpointRadius(): number { return MIDPOINT_RADIUS_PX * this.unitsPerPx; }
  /**
   * Label size for a room: the normal 13px on screen, shrunk to fit inside the room when the
   * floor plan is shown small (phone, narrow window, zoomed out). Null = too small to read.
   */
  labelFontFor(item: RenderedRoom): number | null {
    const font = Math.min(LABEL_FONT_PX * this.unitsPerPx, item.fitFont);
    return font >= MIN_LABEL_FONT_PX * this.unitsPerPx ? font : null;
  }

  trackByRoom(_: number, item: RenderedRoom): string {
    return item.room.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ───────── Pointer handling ─────────

  onSvgPointerDown(event: PointerEvent): void {
    if (!this.editable || event.button !== 0) {
      return;
    }

    const point = this.toImagePoint(event);
    if (!point) return;

    if (this.tool === 'rectangle') {
      event.preventDefault();
      this.svgRef.nativeElement.setPointerCapture(event.pointerId);
      this.rectStart = point;
      this.rectEnd = point;
      return;
    }

    if (this.tool === 'polygon') {
      event.preventDefault();
      // Second click of a double-click, or a click on the first vertex, closes the shape
      if (this.draft.length >= 3 && (event.detail >= 2 || this.canClosePolygon)) {
        this.finishPolygon();
        return;
      }
      if (event.detail >= 2) return;
      this.draft = [...this.draft, point];
      return;
    }

    // Select tool: a click on empty space clears the selection
    this.roomSelect.emit(null);
  }

  onRoomPointerDown(event: PointerEvent, room: FloorRoom): void {
    if (!this.editable || this.tool !== 'select' || event.button !== 0) {
      return;
    }
    event.stopPropagation();
    if (room.id !== this.selectedRoomId) {
      this.roomSelect.emit(room.id);
    }
  }

  onVertexPointerDown(event: PointerEvent, index: number): void {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    this.svgRef.nativeElement.setPointerCapture(event.pointerId);
    this.draggingVertex = index;
  }

  /** Dragging an edge midpoint inserts a new vertex there. */
  onMidpointPointerDown(event: PointerEvent, edgeIndex: number): void {
    if (event.button !== 0 || !this.editPoints) return;
    event.stopPropagation();
    event.preventDefault();

    const next = [...this.editPoints];
    next.splice(edgeIndex + 1, 0, roundPoint(this.midpoints[edgeIndex]));
    this.editPointsChange.emit(next);

    this.svgRef.nativeElement.setPointerCapture(event.pointerId);
    this.draggingVertex = edgeIndex + 1;
  }

  /** Double-clicking a vertex removes it (a polygon keeps at least 3). */
  onVertexDoubleClick(event: MouseEvent, index: number): void {
    event.stopPropagation();
    if (!this.editPoints || this.editPoints.length <= 3) return;
    this.editPointsChange.emit(this.editPoints.filter((_, i) => i !== index));
  }

  onSvgPointerMove(event: PointerEvent): void {
    if (!this.editable) return;

    const point = this.toImagePoint(event);
    this.cursor = point;
    if (!point) return;

    if (this.rectStart) {
      this.rectEnd = point;
    } else if (this.draggingVertex !== null && this.editPoints) {
      const next = [...this.editPoints];
      next[this.draggingVertex] = point;
      this.editPointsChange.emit(next);
    }
  }

  onSvgPointerUp(event: PointerEvent): void {
    const svg = this.svgRef.nativeElement;
    if (svg.hasPointerCapture(event.pointerId)) {
      svg.releasePointerCapture(event.pointerId);
    }

    this.draggingVertex = null;

    if (this.rectStart && this.rectEnd) {
      const minSize = MIN_RECT_SIZE_PX * this.unitsPerPx;
      const wide = Math.abs(this.rectEnd.x - this.rectStart.x) >= minSize;
      const tall = Math.abs(this.rectEnd.y - this.rectStart.y) >= minSize;
      if (wide && tall) {
        this.shapeDrawn.emit(this.rectCorners(this.rectStart, this.rectEnd));
      }
      this.rectStart = null;
      this.rectEnd = null;
    }
  }

  onSvgPointerLeave(): void {
    if (!this.rectStart && this.draggingVertex === null) {
      this.cursor = null;
      this.snapPoint = null;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.editable || !this.isDrawing) return;

    const target = event.target as HTMLElement | null;
    if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelDrawing();
    } else if (event.key === 'Enter' && this.draft.length >= 3) {
      event.preventDefault();
      this.finishPolygon();
    } else if (event.key === 'Backspace' && this.draft.length > 0) {
      event.preventDefault();
      this.draft = this.draft.slice(0, -1);
    }
    this.cdr.markForCheck();
  }

  cancelDrawing(): void {
    this.draft = [];
    this.rectStart = null;
    this.rectEnd = null;
    this.snapPoint = null;
    this.draggingVertex = null;
  }

  // ───────── Internals ─────────

  private finishPolygon(): void {
    if (this.draft.length >= 3) {
      this.shapeDrawn.emit(this.draft);
    }
    this.draft = [];
    this.snapPoint = null;
  }

  /** Pointer → image pixel coordinates, clamped to the image and snapped (hold Alt to disable). */
  private toImagePoint(event: PointerEvent): RoomPoint | null {
    const rect = this.svgRef.nativeElement.getBoundingClientRect();
    if (!rect.width || !rect.height || !this.imageWidth || !this.imageHeight) {
      return null;
    }

    const raw = clampToBounds({
      x: ((event.clientX - rect.left) / rect.width) * this.imageWidth,
      y: ((event.clientY - rect.top) / rect.height) * this.imageHeight
    }, this.imageWidth, this.imageHeight);

    // The drawn rectangle size uses the live rect so it stays correct mid zoom-transition
    this.unitsPerPx = this.imageWidth / rect.width;

    if (event.altKey) {
      this.snapPoint = null;
      return roundPoint(raw);
    }

    const radius = SNAP_RADIUS_PX * this.unitsPerPx;
    // Snap to other rooms, plus the draft's own first vertex so polygons close exactly
    const snapTargets: Pick<FloorRoom, 'id' | 'points'>[] = this.draft.length
      ? [...this.rooms, { id: '__draft__', points: [this.draft[0]] }]
      : this.rooms;
    const excludeId = this.draggingVertex !== null ? this.selectedRoomId : null;

    const { point, snapped } = snapToRooms(raw, snapTargets, radius, excludeId);
    this.snapPoint = snapped ? point : null;
    return roundPoint(point);
  }

  private rectCorners(a: RoomPoint, b: RoomPoint): RoomPoint[] {
    const left = Math.min(a.x, b.x);
    const right = Math.max(a.x, b.x);
    const top = Math.min(a.y, b.y);
    const bottom = Math.max(a.y, b.y);
    return [
      { x: left, y: top },
      { x: right, y: top },
      { x: right, y: bottom },
      { x: left, y: bottom }
    ];
  }

  private buildRenderModel(): void {
    this.renderedRooms = this.rooms.map((room, index) => {
      const points = room.id === this.selectedRoomId && this.editPoints ? this.editPoints : room.points;
      const placement = points.length >= 3
        ? this.placeLabel(points, room.name)
        : { label: points[0] ?? { x: 0, y: 0 }, fitFont: 0 };
      return {
        room,
        color: roomColor(room, index),
        points: toSvgPoints(points),
        ...placement
      };
    });

    const edit = this.editPoints ?? [];
    this.midpoints = edit.map((p, i) => {
      const next = edit[(i + 1) % edit.length];
      return { x: (p.x + next.x) / 2, y: (p.y + next.y) / 2 };
    });
  }

  /**
   * Picks where the name goes: the room's centre when it fits well there, otherwise the spot
   * (from a 16x16 grid over the room) where the name can be largest. Thin or L-shaped rooms
   * such as corridors put the label along their widest part instead of hiding it.
   */
  private placeLabel(points: RoomPoint[], name: string): { label: RoomPoint; fitFont: number } {
    const centre = labelPoint(points);
    const centreFit = this.fitLabelFont(points, centre, name);
    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const steps = 16;

    const candidates: { label: RoomPoint; fitFont: number }[] = [];
    for (let i = 1; i < steps; i++) {
      for (let j = 1; j < steps; j++) {
        const candidate = {
          x: minX + ((maxX - minX) * i) / steps,
          y: minY + ((maxY - minY) * j) / steps
        };
        if (polygonContains(points, candidate)) {
          candidates.push({ label: candidate, fitFont: this.fitLabelFont(points, candidate, name) });
        }
      }
    }

    const bestFit = Math.max(centreFit, ...candidates.map(c => c.fitFont));

    // Keep the centred position unless another spot gives a clearly bigger label
    if (centreFit >= bestFit * 0.8) {
      return { label: centre, fitFont: centreFit };
    }

    // Many spots along a corridor fit about equally: take the one nearest the centre
    return candidates
      .filter(c => c.fitFont >= bestFit * 0.95)
      .reduce((a, b) => (distance(a.label, centre) <= distance(b.label, centre) ? a : b));
  }

  /** Fits the name's width (with 10% padding) and caps its height at 60% of the room's. */
  private fitLabelFont(points: RoomPoint[], label: RoomPoint, name: string): number {
    const width = centeredSpan(points, label, 'horizontal') * 0.9;
    const height = centeredSpan(points, label, 'vertical') * 0.6;
    const chars = Math.max((name || '').length, 1);
    return Math.max(0, Math.min(width / (chars * LABEL_CHAR_WIDTH), height));
  }

  private updateScale(): void {
    const displayWidth = (this.layoutWidth || this.svgRef?.nativeElement.clientWidth || 0) * (this.zoom || 1);
    if (displayWidth > 0 && this.imageWidth > 0) {
      this.unitsPerPx = this.imageWidth / displayWidth;
    }
  }
}
